import { ReactElement, useRef, useState, type HTMLAttributes } from 'react'
import { CUSTOM_STOREY_IMAGE_PAPER_FORMAT, DEFAULT_CUSTOM_PAPER_WIDTH_CM } from '@constants'
import {
  CloudUpload as UploadIcon,
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Settings,
} from '@mui/icons-material'
import MapIcon from '@mui/icons-material/Map'
import {
  Button,
  IconButton,
  Select,
  MenuItem,
  Stack,
  TextField,
  Popover,
  Tooltip,
} from '@mui/material'
import { InfoTooltip } from '@ui/feedback'
import { useStoreyImageOriginTool } from '@editorHooks'
import StoreyImageConfigCopyDialog from './StoreyImageConfigCopyDialog'
import StoreyImageOffsetField from './StoreyImageOffsetField'
import StoreyImagePdfPageDialog from './StoreyImagePdfPageDialog'
import { formatStoreyImagePdfPageSizeInfo } from './utils'

type TransparencyField = keyof StoreyImageTransparencyModeConfig

const isPdfFile = (file: File): boolean =>
  file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')

interface ImageUploadControlsProps {
  storey: string
  onUpload: (file: File, storey: string, options?: { pdfPage?: number }) => void
  onRemove: (storey: string) => void
  onUpdateTransform: (storey: string, transform: StoreyImageTransform) => void
  onToggleVisibility2D: (storey: string) => void
  onToggleVisibility3D: (storey: string) => void
  hasImage: boolean
  isVisible2D: boolean
  isVisible3D: boolean
  isOrthographic: boolean
  transform: StoreyImageTransform
  isUploading: boolean
  isRemoving: boolean
}

const PAPER_FORMATS = [
  { value: 'A0', label: 'A0' },
  { value: 'A1', label: 'A1' },
  { value: 'A2', label: 'A2' },
  { value: 'A3', label: 'A3' },
  { value: 'A4', label: 'A4' },
  { value: CUSTOM_STOREY_IMAGE_PAPER_FORMAT, label: 'Andere' },
]

const StoreyImageUpload = ({
  storey,
  onUpload,
  onRemove,
  onUpdateTransform,
  onToggleVisibility2D,
  onToggleVisibility3D,
  hasImage,
  isVisible2D,
  isVisible3D,
  isOrthographic,
  transform,
  isUploading,
  isRemoving,
}: ImageUploadControlsProps): ReactElement => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [pendingPdfFile, setPendingPdfFile] = useState<File | null>(null)

  const handleFileUpload = () => {
    fileInputRef.current?.click()
  }

  const resetFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    if (isPdfFile(file)) {
      setPendingPdfFile(file)
      return
    }

    onUpload(file, storey)
    resetFileInput()
  }

  const handlePdfPageConfirm = (pdfPage: number) => {
    if (!pendingPdfFile) return
    onUpload(pendingPdfFile, storey, { pdfPage })
    setPendingPdfFile(null)
    resetFileInput()
  }

  const handlePdfPageDialogClose = () => {
    setPendingPdfFile(null)
    resetFileInput()
  }

  const handleTransformChange = (field: keyof StoreyImageTransform, value: number | string) => {
    if (field === 'scale') {
      if (typeof value !== 'number' || !Number.isFinite(value)) return
      value = Math.max(1, value)
    }

    if (field === 'paperWidthCm') {
      if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) return
    }

    onUpdateTransform(storey, {
      ...transform,
      [field]: value,
      offsetsInPdfCm: true,
    })
  }

  const handlePaperFormatChange = (paperFormat: string) => {
    if (paperFormat === CUSTOM_STOREY_IMAGE_PAPER_FORMAT) {
      onUpdateTransform(storey, {
        ...transform,
        paperFormat,
        paperWidthCm: transform.paperWidthCm ?? DEFAULT_CUSTOM_PAPER_WIDTH_CM,
        offsetsInPdfCm: true,
      })
      return
    }

    handleTransformChange('paperFormat', paperFormat)
  }

  const isCustomPaperFormat = transform.paperFormat === CUSTOM_STOREY_IMAGE_PAPER_FORMAT
  const pdfPageSizeInfo = formatStoreyImagePdfPageSizeInfo(transform)

  const transparencyMode = isOrthographic ? '2D' : '3D'
  const transparencySettings = transform.transparencyConfig[transparencyMode]

  const handleTransparencyChange = (field: TransparencyField, value: number) => {
    onUpdateTransform(storey, {
      ...transform,
      transparencyConfig: {
        ...transform.transparencyConfig,
        [transparencyMode]: {
          ...transparencySettings,
          [field]: value,
        },
      },
    })
  }

  const clampTransparency = (value: number): number => Math.max(0, Math.min(100, value))

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const [isCopyConfigDialogOpen, setIsCopyConfigDialogOpen] = useState(false)

  const { start: startOriginTool } = useStoreyImageOriginTool({
    storey,
    transform,
    onUpdateTransform,
  })

  // Take over another storey image's configuration, keeping everything file-specific untouched.
  const handleCopyConfigFrom = (source: StoreyImageTransform) => {
    onUpdateTransform(storey, {
      ...transform,
      scale: source.scale,
      offsetX: source.offsetX,
      offsetY: source.offsetY,
      rotation: source.rotation,
      paperFormat: source.paperFormat,
      paperWidthCm: source.paperWidthCm,
      transparencyConfig: source.transparencyConfig,
      offsetsInPdfCm: true,
    })
  }

  const isVisible = isOrthographic ? isVisible2D : isVisible3D
  const onToggleVisibility = isOrthographic ? onToggleVisibility2D : onToggleVisibility3D

  return (
    <>
      <Stack direction="row" spacing={1}>
        {hasImage ? (
          <IconButton
            data-cy={`btn-storey-${storey}-image-delete`}
            size="small"
            onClick={() => onRemove(storey)}
            sx={{ color: 'error.main', padding: 0 }}
            loading={isRemoving}
            disabled={isRemoving}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        ) : (
          <Tooltip title="Stockwerksfolie hochladen">
            <span>
              <IconButton
                data-cy={`btn-storey-${storey}-image-upload`}
                size="small"
                aria-label="Stockwerksfolie hochladen"
                onClick={handleFileUpload}
                loading={isUploading}
                disabled={isUploading}
                sx={{ padding: 0 }}
              >
                <UploadIcon color="disabled" fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}

        {hasImage && (
          <>
            {isOrthographic && (
              <IconButton
                size="small"
                onClick={e => {
                  e.stopPropagation()
                  onToggleVisibility2D(storey)
                }}
                sx={{ color: isVisible2D ? 'success.main' : 'text.disabled', padding: 0 }}
              >
                {isVisible2D ? (
                  <MapIcon fontSize="small" />
                ) : (
                  <MapIcon fontSize="small" color="disabled" />
                )}
              </IconButton>
            )}

            {!isOrthographic && (
              <IconButton
                size="small"
                onClick={e => {
                  e.stopPropagation()
                  onToggleVisibility3D(storey)
                }}
                sx={{ color: isVisible3D ? 'success.main' : 'text.disabled', padding: 0 }}
              >
                {isVisible3D ? (
                  <MapIcon fontSize="small" />
                ) : (
                  <MapIcon fontSize="small" color="disabled" />
                )}
              </IconButton>
            )}

            <IconButton
              data-cy={`btn-storey-${storey}-image-settings`}
              onClick={event => {
                event.stopPropagation()
                setAnchorEl(event.currentTarget)
              }}
              size="small"
              sx={{ padding: 0 }}
            >
              <Settings fontSize="small" />
            </IconButton>
          </>
        )}
      </Stack>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        data-cy={`input-storey-${storey}-image-upload`}
        type="file"
        accept="image/*,application/pdf,.pdf"
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />

      <StoreyImagePdfPageDialog
        open={pendingPdfFile !== null}
        file={pendingPdfFile}
        storey={storey}
        onConfirm={handlePdfPageConfirm}
        onClose={handlePdfPageDialogClose}
      />

      <StoreyImageConfigCopyDialog
        open={isCopyConfigDialogOpen}
        storey={storey}
        onClose={() => setIsCopyConfigDialogOpen(false)}
        onSelect={handleCopyConfigFrom}
      />

      <Popover
        open={!!anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorEl={anchorEl || undefined}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          padding={1}
          onClick={e => e.stopPropagation()}
        >
          {hasImage && (
            <>
              {/* Visibility toggle button */}
              <IconButton
                size="small"
                onClick={e => {
                  e.stopPropagation()
                  onToggleVisibility(storey)
                }}
                sx={{ color: isVisible ? 'success.main' : 'text.disabled' }}
              >
                {isVisible ? (
                  <VisibilityIcon fontSize="small" />
                ) : (
                  <VisibilityOffIcon fontSize="small" />
                )}
              </IconButton>
            </>
          )}

          {/* Paper Format */}
          <Select
            value={transform.paperFormat}
            onChange={e => {
              e.stopPropagation()
              handlePaperFormatChange(e.target.value)
            }}
            size="small"
            SelectDisplayProps={
              {
                'data-cy': `select-storey-${storey}-image-paper-format`,
              } as HTMLAttributes<HTMLDivElement>
            }
            MenuProps={{
              disablePortal: true,
            }}
          >
            {PAPER_FORMATS.map(format => (
              <MenuItem
                key={format.value}
                value={format.value}
                data-cy={`select-storey-${storey}-image-paper-format-option-${format.value}`}
                onClick={e => e.stopPropagation()}
              >
                {format.label}
              </MenuItem>
            ))}
          </Select>

          {isCustomPaperFormat && (
            <TextField
              label="Breite [cm]"
              type="number"
              size="small"
              value={transform.paperWidthCm ?? DEFAULT_CUSTOM_PAPER_WIDTH_CM}
              inputProps={{
                step: 1,
                min: 0.1,
                'data-cy': `input-storey-${storey}-image-paper-width-cm`,
              }}
              onChange={e => {
                e.stopPropagation()
                const paperWidthCm = parseFloat(e.target.value)
                // Ignore transient empty/invalid values from controlled number inputs while typing.
                if (!Number.isFinite(paperWidthCm) || paperWidthCm <= 0) return
                handleTransformChange('paperWidthCm', paperWidthCm)
              }}
              onClick={e => {
                e.stopPropagation()
              }}
              sx={{ width: 100 }}
            />
          )}

          {/* Scale */}
          <TextField
            label="1:M"
            type="number"
            size="small"
            value={transform.scale}
            inputProps={{
              step: 5,
              min: 1,
              'data-cy': `input-storey-${storey}-image-scale`,
            }}
            onChange={e => {
              e.stopPropagation()
              handleTransformChange('scale', parseFloat(e.target.value))
            }}
            onClick={e => {
              e.stopPropagation()
              e.preventDefault()
            }}
            sx={{ width: 75 }}
          />

          {/* Offset X */}
          <StoreyImageOffsetField
            label="X [cm, PDF]"
            value={transform.offsetX}
            step={0.1}
            dataCy={`input-storey-${storey}-image-offset-x`}
            onChange={value => handleTransformChange('offsetX', value)}
          />

          {/* Offset Y */}
          <StoreyImageOffsetField
            label="Y [cm, PDF]"
            value={transform.offsetY}
            step={0.1}
            dataCy={`input-storey-${storey}-image-offset-y`}
            onChange={value => handleTransformChange('offsetY', value)}
          />

          {/* Rotation */}
          <TextField
            label="Drehung"
            type="number"
            size="small"
            value={transform.rotation}
            onChange={e => {
              e.stopPropagation()
              handleTransformChange('rotation', parseFloat(e.target.value))
            }}
            inputProps={{
              step: 5,
              min: -180,
              max: 180,
              'data-cy': `input-storey-${storey}-image-rotation`,
            }}
            onClick={e => {
              e.stopPropagation()
              e.preventDefault()
            }}
            sx={{ width: 75 }}
          />

          {/* Foreground transparency */}
          <TextField
            label="Vordergrund"
            type="number"
            size="small"
            value={transparencySettings.foreground}
            onChange={e => {
              e.stopPropagation()
              const value = parseInt(e.target.value)
              handleTransparencyChange('foreground', isNaN(value) ? 0 : clampTransparency(value))
            }}
            inputProps={{
              step: 5,
              min: 0,
              max: 100,
              'data-cy': `input-storey-${storey}-image-foreground-transparency`,
            }}
            onClick={e => {
              e.stopPropagation()
              e.preventDefault()
            }}
            sx={{ width: 90 }}
          />

          {/* Background transparency */}
          <TextField
            label="Hintergrund"
            type="number"
            size="small"
            value={transparencySettings.background}
            onChange={e => {
              e.stopPropagation()
              const value = parseInt(e.target.value)
              handleTransparencyChange('background', isNaN(value) ? 0 : clampTransparency(value))
            }}
            inputProps={{
              step: 5,
              min: 0,
              max: 100,
              'data-cy': `input-storey-${storey}-image-background-transparency`,
            }}
            onClick={e => {
              e.stopPropagation()
              e.preventDefault()
            }}
            sx={{ width: 90 }}
          />

          {pdfPageSizeInfo && <InfoTooltip text={pdfPageSizeInfo} headline="PDF-Metadaten" />}

          {/* Pick the global origin on the plan via the scene */}
          <Button
            size="small"
            variant="contained"
            data-cy={`btn-storey-${storey}-image-origin-in-scene`}
            sx={{ flexShrink: 0 }}
            onClick={e => {
              e.stopPropagation()
              setAnchorEl(null)
              startOriginTool()
            }}
          >
            In Szene
          </Button>

          {/* Take over the configuration from another storey's image */}
          <Button
            size="small"
            variant="contained"
            data-cy={`btn-storey-${storey}-image-copy-config`}
            sx={{ flexShrink: 0 }}
            onClick={e => {
              e.stopPropagation()
              setIsCopyConfigDialogOpen(true)
            }}
          >
            Von anderem
          </Button>
        </Stack>
      </Popover>
    </>
  )
}

export default StoreyImageUpload
